import {
  Button,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@e2n/ui';
import {
  faCheck,
  faCircleExclamation,
  faKey,
  faLock,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { DeaktivierenDialog } from '../components/DeaktivierenDialog.tsx';
import { useQuery } from '@apollo/client/react';
import { GET_ZUGAENGE } from '../lib/graphql/queries.ts';
import { ZugaengeTable } from '../components/ZugaengeTable.tsx';
import { ZugaengeListe } from '../components/ZugaengeListe.tsx';
import { useState } from 'react';
import { Hinweis, SeitenHinweis } from '../components/Hinweis.tsx';
import { useNavigate } from 'react-router-dom';

export function ZugaengePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deaktivierenOffen, setDeaktivierenOffen] = useState(false);
  const { data, loading, error } = useQuery(GET_ZUGAENGE);

  if (loading) return <Skeleton className="m-10 h-64" />;
  if (error || !data) {
    return (
      <SeitenHinweis
        icon={faCircleExclamation}
        art="fehler"
        titel={t('domain.e2nApi.fehler.laden.titel')}
        beschreibung={t('domain.e2nApi.fehler.laden.beschreibung')}
      />
    );
  }

  const result = data.zugaenge;
  if (result.__typename !== 'ZugangQuerySuccess') {
    return (
      <SeitenHinweis
        icon={faLock}
        titel={t('domain.e2nApi.fehler.keinAdmin.titel')}
        beschreibung={t('domain.e2nApi.fehler.keinAdmin.beschreibung')}
      />
    );
  }

  const zugaenge = result.zugaenge ?? [];
  const limitErreicht = zugaenge.length >= result.zugangLimit;

  return (
    <div className="p-4 md:p-10 2xl:mx-auto 2xl:max-w-screen-2xl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-extrabold text-neutrals-900">
            {t('domain.e2nApi.zugaenge.title')}
          </h1>
          <p className="mt-3 text-sm text-neutrals-800">
            {t('domain.e2nApi.zugaenge.description')}
          </p>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={limitErreicht ? 0 : -1}>
              <Button
                size="icon"
                disabled={limitErreicht}
                className="fixed right-4 bottom-4 z-10 md:static"
                aria-label={t('domain.e2nApi.zugaenge.neuerZugang')}
                onClick={() => navigate('zugang/neu')}>
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </span>
          </TooltipTrigger>
          {limitErreicht && (
            <TooltipContent
              side="bottom"
              align="end"
              className="w-72 border-neutrals-200 p-2 text-neutrals-900">
              {t('domain.e2nApi.zugaenge.tabelle.limitTooltip', {
                limit: result.zugangLimit,
              })}
            </TooltipContent>
          )}
        </Tooltip>
      </div>

      {zugaenge.length === 0 ? (
        <Hinweis
          className="mt-6"
          icon={faKey}
          titel={t('domain.e2nApi.zugaenge.empty.title')}
          beschreibung={t('domain.e2nApi.zugaenge.empty.description')}
        />
      ) : (
        <>
          <Card className="mt-6 hidden md:block">
            <CardContent className="p-5">
              <ZugaengeTable
                zugaenge={zugaenge}
                zugangLimit={result.zugangLimit}
              />
            </CardContent>
          </Card>

          <div className="mt-6 md:hidden">
            <ZugaengeListe
              zugaenge={zugaenge}
              zugangLimit={result.zugangLimit}
            />
          </div>
        </>
      )}

      <Card className="mt-8">
        <CardContent className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
          <span className="flex items-center gap-2 text-sm text-neutrals-900">
            <FontAwesomeIcon icon={faCheck} />
            {t('domain.e2nApi.zugaenge.aktivierung.title')}
          </span>
          <Button
            variant="outline-secondary"
            className="w-full md:w-auto"
            onClick={() => setDeaktivierenOffen(true)}>
            {t('domain.e2nApi.zugaenge.aktivierung.deaktivieren')}
          </Button>
        </CardContent>
      </Card>

      <DeaktivierenDialog
        open={deaktivierenOffen}
        onOpenChange={setDeaktivierenOffen}
      />
    </div>
  );
}
